import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import api from '../api';

const TasksContext = createContext(null);

export function TasksProvider({ children }) {
  const [tasksByYear, setTasksByYear] = useState({});
  const inFlight = useRef({});

  const fetchYear = useCallback((year) => {
    if (inFlight.current[year]) return inFlight.current[year];
    const promise = api.get(`/tasks?year=${year}`)
      .then(res => {
        setTasksByYear(prev => ({ ...prev, [year]: res.data }));
        return res.data;
      })
      .finally(() => { delete inFlight.current[year]; });
    inFlight.current[year] = promise;
    return promise;
  }, []);

  // Primjenjuje patch na sve keširane godine — task ID je globalno jedinstven,
  // a caller (Home/Dashboard) ne zna koje su godine trenutno keširane kod drugih stranica.
  // Ako je zakrpani zadatak nečiji podzadatak, ažurira i njegov unos unutar roditeljevog
  // keširanog `subtasks` niza — inače roditelj i dalje prikazuje staro (npr. "Nije započeto")
  // dok se ručno ne refetcha, jer se ovaj patch inače tiče samo objekta s odgovarajućim id-om.
  const patchTask = useCallback((patch) => {
    setTasksByYear(prev => {
      const next = {};
      for (const [year, list] of Object.entries(prev)) {
        next[year] = list.map(t => {
          if (t.id === patch.taskId) {
            return {
              ...t,
              status: patch.taskStatus || t.status,
              assignments: t.assignments?.map(a => a.email === patch.email ? { ...a, work_status: patch.work_status } : a),
            };
          }
          if ((patch.taskStatus || patch.work_status) && t.subtasks?.some(st => st.id === patch.taskId)) {
            return {
              ...t,
              subtasks: t.subtasks.map(st => {
                if (st.id !== patch.taskId) return st;
                const status = patch.taskStatus || st.status;
                // work_status u sažetku je izveden: 'done' ako je zadatak zatvoren, inače 'in_progress'
                // čim itko krene; ne spuštamo ga na 'pending' jer ovaj patch ne zna za ostale sudionike
                const work_status = status === 'done'
                  ? 'done'
                  : (patch.work_status && patch.work_status !== 'pending' ? patch.work_status : st.work_status);
                return { ...st, status, work_status };
              }),
            };
          }
          return t;
        });
      }
      return next;
    });
  }, []);

  return (
    <TasksContext.Provider value={{ tasksByYear, fetchYear, patchTask }}>
      {children}
    </TasksContext.Provider>
  );
}

export function useTasks() {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error('useTasks mora biti unutar TasksProvider');
  return ctx;
}
